'use client';

import type { Ref } from 'react';
import { Box } from '@mui/material';
import { TableCell, TableRow, Typography } from '@/primitives';
import {
  type EbvAverageData,
  type EbvConfigItem,
  type EbvGroupingOptions,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { Ilr2MixedHtmlLabel } from '@/components/publicSearch/Ilr2MixedHtmlLabel';
import { getBreedAverageRaw, getEbvCellContext } from './helpers';
import BreedAverageEbvCell from '@/components/publicSearch/animal/ebv/BreedAverageEbvCell';
import {
  BREED_AVERAGE_FALLBACK_LABEL,
  Z_BREED_EBV,
  Z_BREED_NAME,
} from './constants';
import type { MatingTableChrome } from './useMatingTableChrome';

interface BreedAverageRowProps {
  breedAveragesRowRef: Ref<HTMLTableRowElement>;
  ebvAverage: EbvAverageData;
  visibleEbvs: EbvConfigItem[];
  groupStartFields: Set<string>;
  chrome: MatingTableChrome;
  /** Sticky offset (px) below the header so this row pins under it while scrolling. */
  stickyHeaderHeight: number;
  currencySymbol: string;
  societyId?: string | number;
  ebvGroupingOptions?: EbvGroupingOptions;
}

/** Sticky "Breed Average" reference row shown above the mating pairs (when the society has EBV averages). */
export default function BreedAverageRow({
  breedAveragesRowRef,
  ebvAverage,
  visibleEbvs,
  groupStartFields,
  chrome,
  stickyHeaderHeight,
  currencySymbol,
  societyId,
  ebvGroupingOptions,
}: BreedAverageRowProps) {
  return (
    <TableRow ref={breedAveragesRowRef}>
      <TableCell
        sx={{
          position: 'sticky',
          left: 0,
          top: stickyHeaderHeight,
          zIndex: Z_BREED_NAME,
          minWidth: 200,
          maxWidth: 800,
          backgroundColor: 'background.paper',
          py: 1,
          borderBottom: chrome.cellBorder,
          whiteSpace: 'nowrap',
          boxShadow: chrome.stickyColShadow,
        }}
      >
        <Typography
          component="span"
          variant="bodymedium"
          sx={{
            fontSize: '0.8125rem',
            fontStyle: 'italic',
            color: 'text.secondary',
          }}
        >
          {typeof ebvAverage.avgCaptionB === 'string' ||
          typeof ebvAverage.avgCaptionR === 'string' ? (
            <Box
              component="span"
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
            >
              {typeof ebvAverage.avgCaptionB === 'string' ? (
                <Ilr2MixedHtmlLabel
                  raw={ebvAverage.avgCaptionB}
                  societyId={societyId}
                >
                  {(plain) => <span>{plain}</span>}
                </Ilr2MixedHtmlLabel>
              ) : null}
              {typeof ebvAverage.avgCaptionR === 'string' ? (
                <Ilr2MixedHtmlLabel
                  raw={ebvAverage.avgCaptionR}
                  societyId={societyId}
                >
                  {(plain) => <span>{plain}</span>}
                </Ilr2MixedHtmlLabel>
              ) : null}
            </Box>
          ) : (
            (ebvAverage.avgsCaption ?? BREED_AVERAGE_FALLBACK_LABEL)
          )}
        </Typography>
      </TableCell>
      {visibleEbvs.map((ebvConfig, index) => {
        const { highlighted, prevHighlighted, tint, previousSameCategory } =
          getEbvCellContext(
            ebvConfig,
            visibleEbvs[index - 1],
            ebvGroupingOptions,
          );
        const breedAvgRaw = getBreedAverageRaw(ebvAverage, ebvConfig);
        return (
          <TableCell
            key={ebvConfig.field}
            align="center"
            sx={{
              position: 'sticky',
              top: stickyHeaderHeight,
              zIndex: Z_BREED_EBV,
              fontSize: '0.8125rem',
              py: 1,
              borderBottom: chrome.cellBorder,
              fontStyle: 'italic',
              color: 'text.secondary',
              backgroundColor: 'background.paper',
              ...(groupStartFields.has(ebvConfig.field) && {
                borderLeft: chrome.cellBorder,
              }),
              ...(highlighted && {
                borderRight: chrome.ebvHighlightBorder,
                ...(prevHighlighted
                  ? {}
                  : {
                      borderLeft: chrome.ebvHighlightBorder,
                    }),
              }),
              ...(!highlighted && {
                backgroundColor: chrome.categoryCellBg(tint),
                borderRight: chrome.categoryBorder(tint),
                ...(previousSameCategory
                  ? {}
                  : {
                      borderLeft: chrome.categoryBorder(tint),
                    }),
              }),
            }}
          >
            {breedAvgRaw !== undefined &&
            breedAvgRaw !== null &&
            breedAvgRaw !== '' ? (
              <BreedAverageEbvCell
                raw={breedAvgRaw}
                format={ebvConfig.format || '1'}
                currencySymbol={currencySymbol}
              />
            ) : null}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
